import { useApi } from '../api/client.js';
import { Card } from '../components/ui.js';
import { useAsync } from '../hooks/useAsync.js';

function formatNumber(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

/**
 * Shows the attribution of the last 7 days of `verdict='bot'` clicks.
 *
 * - **KV scanner cascade** — a honeypot click earlier in the window set a
 *   90-second flag; subsequent clicks from the same (ASN, IP) for that
 *   send inherit 'bot'.
 * - **Heuristic (empty UA + scanner ASN)** — the click-handler's narrow
 *   rule caught a scanner (Microsoft Safe Links is the common case) that
 *   the honeypot missed.
 * - **Unattributed** — rows written before the classifier column was
 *   added, or future classifier values not yet known to this build.
 *
 * A row with classifier='honeypot' implies verdict='honeypot', so it
 * doesn't show here; see the top-row "Honeypot hits" metric instead.
 */
export function BotBreakdown() {
  const api = useApi();
  const { state } = useAsync(() => api.metrics(), [api]);

  if (state.status === 'loading') {
    return (
      <Card title="Bot clicks, 7 days">
        <div className="h-20 animate-pulse" style={{ background: 'var(--bg-secondary)' }} />
      </Card>
    );
  }

  if (state.status === 'error') {
    return (
      <Card title="Bot clicks, 7 days">
        <div role="alert" className="text-[13px]" style={{ color: 'var(--danger-text)' }}>
          Couldn't load bot breakdown: {state.error.message}
        </div>
      </Card>
    );
  }

  const b = state.data.botBreakdown7d ?? {
    honeypot: 0,
    kv_scanner: 0,
    heuristic: 0,
    unattributed: 0,
  };
  const total = b.kv_scanner + b.heuristic + b.unattributed;

  const rows: Array<{ label: string; tooltip: string; count: number }> = [
    {
      label: 'KV scanner cascade',
      tooltip: 'Clicks after a honeypot hit set the 90s scanner flag for this send.',
      count: b.kv_scanner,
    },
    {
      label: 'Heuristic (empty UA + scanner ASN)',
      tooltip:
        'M365 Safe Links and similar — caught at click time by the narrow ASN + empty-UA rule.',
      count: b.heuristic,
    },
  ];
  if (b.unattributed > 0) {
    rows.push({
      label: 'Unattributed (legacy)',
      tooltip:
        'Bot rows written before the classifier column was added. Will drop to zero after 7 days.',
      count: b.unattributed,
    });
  }

  return (
    <Card title={`Bot clicks, 7 days · ${formatNumber(total)} total`}>
      {total === 0 ? (
        <p className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
          No bot clicks filtered in the last 7 days.
        </p>
      ) : (
        <ul className="divide-y" style={{ borderColor: 'var(--border)' }}>
          {rows.map((r) => {
            const pct = total > 0 ? Math.round((r.count / total) * 100) : 0;
            return (
              <li key={r.label} className="py-3" title={r.tooltip}>
                <div className="flex items-baseline justify-between gap-4">
                  <span className="text-[14px] font-medium">{r.label}</span>
                  <span className="text-[14px] tabular-nums">
                    {formatNumber(r.count)}{' '}
                    <span className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>
                      ({pct}%)
                    </span>
                  </span>
                </div>
                <div
                  className="mt-1 h-1.5 rounded-full overflow-hidden"
                  style={{ background: 'var(--bg-secondary)' }}
                >
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${pct}%`,
                      background: 'var(--accent, #4b6bff)',
                    }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
